import React, { Fragment, useEffect, useRef, useState, useCallback, useMemo } from "react";
import Layout from "../../components/biblio/SuiviLayout";
import {
  dateFormat,
  getAuthToken,
  setTitle,
} from "../../helpers/Helper";
import { SITE_URL, API_SUIVI_URL } from "../../helpers/Constants";
import Modal from "../../components/Modal";
import UserStore from "../../store/UserStore";
import Loading from "../../helpers/Loading";
import Select from 'react-select';
import DatePicker from "react-date-picker";
import { Link } from 'react-router-dom'
import { notify } from "../../helpers/Helper";
import UploadPdfDropzone from "../../components/UploadPdfDropzone";
import Quill from '../../components/ReactQuill';
import SecureFileViewer from './utils/SecureFileViewer';
import { fetchBlob, downloadBlob } from './utils/secureFiles';

/**
 * ---- helpers existants / adaptés ----
 */

const apiBase = (API_SUIVI_URL || "").replace(/\/$/, "");

function getSuiviToken() {
  try {
    const raw = localStorage.getItem("bib2z_token_SUIVI");
    return raw ? raw.replace(/^"(.*)"$/, "$1") : null;
  } catch {
    return null;
  }
}
function authHeaders(extra = {}) {
  const token = getSuiviToken();
  return {
    Accept: "application/json",
    ...(extra || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

// IRIs attendus par le backend
const isIri = (v) => typeof v === "string" && (v.includes("/api/") || v.startsWith("/"));

const iri = {
  typeAudit: (id) => (isIri(id) ? id : `/api/type_audits/${id}`),
  audit: (id) => (isIri(id) ? id : `/api/audits/${id}`),
  typeRevue: (id) => (isIri(id) ? id : `/api/type_revues/${id}`),
  structure: (id) => (isIri(id) ? id : `/api/structures/${id}`),
  localite: (id) => (isIri(id) ? id : `/api/localites/${id}`),
  media: (id) => (isIri(id) ? id : `/api/media/${id}`),
  programme: (id) => (isIri(id) ? id : `/api/programmes/${id}`),
  personne: (id) => (isIri(id) ? id : `/api/personnes/${id}`),
  user: (id) => (isIri(id) ? id : `/api/users/${id}`),
};

function adaptRapport(apiItem) {
  const userId = (() => {
    const iri = apiItem?.author?.name || "";
    const parts = iri.split("/");
    return parts.length ? parts[parts.length - 1] : "—";
  })();

  const libelle = apiItem?.mission || apiItem?.code || `Rapport #${apiItem?.id}`;

  const senderAgent = {
    prenom: "",
    nom: `${userId}`,
  };

  const senderStructure = {
    intitule: apiItem?.audit?.title || "—",
  };

  const fichier = !!apiItem?.report?.url;
  const reportUrl = apiItem?.report?.url || null;

  const suiviRecRaports = [];

  return {
    id: apiItem?.id,
    createdAt: apiItem?.createdAt,
    libelle,
    senderAgent,
    senderStructure,
    fichier,
    suiviRecRaports,
    _reportUrl: reportUrl,
    _abstractUrl: apiItem?.abstract?.url || null,
    _annexes: Array.isArray(apiItem?.annexes) ? apiItem.annexes.map(a => a?.url).filter(Boolean) : [],
  };
}

function toAbsoluteUrl(pathOrFull) {
  pathOrFull = pathOrFull.replace("/telechargements", "/media/download").replace("/api/media/download", "/media/download");
  if (!pathOrFull) return null;
  if (pathOrFull.startsWith("http")) return pathOrFull;
  return `${(API_SUIVI_URL || "").replace(/\/$/, "")}${pathOrFull}`;
}

function buildPagination(currentPage, perPage, total) {
  const last = Math.max(1, Math.ceil(total / perPage));
  const current = Math.min(Math.max(Number(currentPage) || 1, 1), last);
  const from = total === 0 ? 0 : (current - 1) * perPage + 1;
  const to = Math.min(total, current * perPage);
  const prevPage = current > 1 ? current - 1 : null;
  const nextPage = current < last ? current + 1 : null;

  return {
    page: current,
    currentPage: current,
    current_page: current,
    perPage,
    per_page: perPage,
    total,
    firstPage: 1,
    first_page: 1,
    prevPage,
    prev_page: prevPage,
    previous: prevPage,
    nextPage,
    next_page: nextPage,
    lastPage: last,
    last_page: last,
    from,
    to,
    hasPrev: !!prevPage,
    hasNext: !!nextPage,
  };
}

/** Petite utilité pour normaliser la valeur des Select contrôlés */
const asOption = (value, label) => (value ? { value, label } : null);

function RowActions({ rapport, onRead, openCommentModal, secureDownload }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  // const navigate = useNavigate();

  const withUrl = (u) => u || null;

  const canReport   = !!rapport?._reportUrl;
  const canAbstract = !!rapport?._abstractUrl;
  const annexes     = Array.isArray(rapport?._annexes) ? rapport._annexes : [];

  return (
    <div ref={rootRef} className="dropdown" style={{position:"relative", display:"inline-block", float: "right"}}>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        Actions ▾
      </button>

      <div
        className={`dropdown-menu dropdown-menu-right ${open ? "show" : ""}`}
        style={{ display: open ? "block" : "none", position: "absolute", right: 0, zIndex: 1000 }}
        role="menu"
      >
        
        <Link className={`dropdown-item`} to={`/suivi-recommandations/read/${rapport?.id}`} onClick={() => setOpen(false)}>
          Voir detail
        </Link>

        <button className={`dropdown-item ${!canReport ? "disabled" : ""}`} type="button" onClick={() => { setOpen(false); onRead?.(rapport?._reportUrl); }}>
          Voir le rapport
        </button>

        {/* <a
          className={`dropdown-item ${!canAbstract ? "disabled" : ""}`}
          href={canAbstract ? withUrl(rapport._abstractUrl) : undefined}
          target="_blank" rel="noreferrer"
          onClick={() => setOpen(false)}
        > */}
        <button className={`dropdown-item ${!canAbstract ? "disabled" : ""}`} type="button" onClick={() => { setOpen(false); onRead?.(rapport?._abstractUrl); }}>
          Voir la synthèse
        </button>

        {annexes.length ? (
          <>
            <div className="dropdown-divider" />
            {annexes.map((u, i) => (
              // <a key={i} className="dropdown-item" href={withUrl(u)} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>
              <button className="dropdown-item" type="button" onClick={() => { setOpen(false); onRead?.(u); }}>
                Voir l’annexe {annexes.length > 1 ? String(i + 1).padStart(2, "0") : ''}
              </button>
            ))}
          </>
        ) : (
          <button className="dropdown-item disabled" type="button">Voir les annexes</button>
        )}

        <div className="dropdown-divider" />
        {/* Navigation SPA : pas de window.location.href */}
        <button className="dropdown-item" type="button" onClick={() => { setOpen(false); window.location.href = '/suivi-recommandations/detail/'+rapport?.id; }}>Visualiser les recommandations</button>
        <button className="dropdown-item" type="button" onClick={() => { setOpen(false); openCommentModal(rapport); }}>Ajouter un commentaire</button>
      </div>
    </div>
  );
}



/**
 * ---- composant principal ----
 */

export default function RapportInspecteur() {
  // table / pagination UI —> pilotés côté API
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [rapports, setRapports] = useState([]);
  const [pagination, setPagination] = useState(buildPagination(1, 10, 0));
  const [loading, setLoading] = useState(true);

  // tri côté API
  const [orderMission, setOrderMission] = useState(null);     // 'asc' | 'desc' | null
  const [orderCreatedAt, setOrderCreatedAt] = useState('desc'); // défaut: récents d'abord
  const [mouseOver, setMouseOver] = useState(null);

  // viewer PDF
  const [viewerSource, setViewerSource] = useState(null); // string | array
  const [showViewer, setShowViewer] = useState(false);
  const token = getAuthToken();

  // Filtres / search (stocke des objets d'option {value,label} pour react-select)
  const [search, setSearch] = useState("");
  const [institution, setInstitution] = useState(null);
  const [typeAudit, setTypeAudit] = useState(null);
  const [structure, setStructure] = useState(null);
  const [audit, setAudit] = useState(null);
  const [typeRevu, setTypeRevu] = useState(null);
  const [inspecteur, setInspecteur] = useState(null);
  const [localite, setLocalite] = useState(null);
  const [date, setDate] = useState(""); // conservé si vous avez un filtre côté API

  // listes pour les <select>
  const [institutionsOptions, setInstitutionsOptions] = useState([]);
  const [typesAuditOptions, setTypesAuditOptions] = useState([]);
  const [structuresOptions, setStructuresOptions] = useState([]);
  const [auditsOptions, setAuditsOptions] = useState([]);
  const [typesRevuOptions, setTypesRevuOptions] = useState([]);
  const [inspecteursOptions, setInspecteursOptions] = useState([]);
  const [localitesOptions, setLocalitesOptions] = useState([]);

  // Modal commentaire 
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentDraft, setCommentDraft] = useState({ file: "", fileId: "", fileName: "", content: "" });
  const [activeRapport, setActiveRapport] = useState(null);
  
  const openCommentModal = (rapport) => {
    setActiveRapport(rapport);
    setCommentDraft({ file: "", fileId: "", fileName: "", content: "" });
    setCommentOpen(true);
  };
  const closeCommentModal = () => setCommentOpen(false);
  
  const canSaveComment = useMemo(
    () => (commentDraft?.content || "").trim().length > 0, // !!commentDraft?.file &&
    [commentDraft]
  );

  const saveComment = async (draft) => {
    try {
      setCommentSaving(true);
      const payload = {
        rapport: `/api/rapports/${activeRapport?.id}`,
        file: draft.file,
        message: draft.content,
      };

      if(!draft.file) delete payload.file;
      
      const res = await fetch(`${apiBase}/comments`, {
        method: "POST",
        headers: { ...authHeaders({ "Content-Type": "application/json" }) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        notify("Erreur", "Échec de l'enregistrement du commentaire.", "error");
        return;
      }
      notify("Succès", "Commentaire ajouté.", "success");
      setCommentOpen(false);
    } catch {
      notify("Erreur", "Impossible d'enregistrer le commentaire.", "error");
    } finally {
      setCommentSaving(false);
    }
  };

  setTitle("RAPPORTS DE SUIVI ET RECOMMANDATION");

  /**
   * buildQueryString:
   * Construction d'une query *API-first* (pagination/tri/filters)
   * Correspond à l'exemple fourni :
   * /api/rapports-inspecteur?page=1&itemsPerPage=10&pagination=true&...&order[mission]=asc&order[createdAt]=desc
   */
  const buildQueryString = useCallback((override = {}) => {
    const params = new URLSearchParams();

    const p = {
      page,
      perPage,
      search,
      institution,
      typeAudit,
      structure,
      audit,
      typeRevu,
      inspecteur,
      localite,
      orderMission,
      orderCreatedAt,
      ...override,
    };

    // Pagination
    params.set("page", String(p.page || 1));
    params.set("itemsPerPage", String(p.perPage || 10));
    params.set("pagination", "true");

    // Filtres (adapter si vos champs exacts diffèrent)
    if (p.search) params.append("mission", p.search); // backend: mission contient la recherche

    if (p.inspecteur?.value) {
      // Exemples: &author=DIMON OU &author[]=/api/programme/1 (votre backend semble accepter divers formats)
      params.append("author", p.inspecteur.label || "");
      params.append("author[]", p.inspecteur.value);
    }

    if (p.audit?.value) params.append("audit", p.audit.value);

    // programme[] multiples — ici Select simple, on envoie la valeur unique
    if (p.typeAudit?.value) params.append("programme[]", decodeURIComponent(p.typeAudit.value || "").trim());

    // localités
    if (p.localite?.value) {
      params.append("localiteAudites", p.localite.value);
      params.append("localiteAudites[]", p.localite.value);
    }

    // structures
    if (p.institution?.value) {
      params.append("structureAudites", p.institution.value);
      params.append("structureAudites[]", p.institution.value);
    }

    // Tri
    if (p.orderMission)   params.append("order[mission]", p.orderMission);
    if (p.orderCreatedAt) params.append("order[createdAt]", p.orderCreatedAt);

    // Exemple de filtre date si votre API l’accepte (adapter la clé si besoin)
    if (date) {
      try {
        const d = new Date(date);
        const iso = d.toISOString();
        const isoDateOnly = iso.slice(0, 10); // YYYY-MM-DD
        console.log(isoDateOnly, p.date);
        params.append("createdAt[after]", isoDateOnly);
      } catch {}
    }

    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [
    page,
    perPage,
    search,
    institution,
    typeAudit,
    structure,
    audit,
    typeRevu,
    inspecteur,
    localite,
    date,
    orderMission,
    orderCreatedAt,
  ]);

  /**
   * fetchRapports (API-first)
   * - Utilise pagination & tri côté serveur
   * - Calcule la pagination à partir de hydra:totalItems si présent
   */
  const fetchRapports = useCallback(async (pageOverride = null) => {
    setLoading(true);
    // alert(page);
    try {
      await UserStore.getCurrentUser().catch(() => {});
      const tokenSuivi = getSuiviToken();
      const slug = UserStore.hasRole('ROLE_INSPECTEUR') ? "inspecteur" : "igm-igf"
      const qs = buildQueryString(pageOverride ? { page: pageOverride } : undefined);
      const url = `${API_SUIVI_URL.replace(/\/$/, "")}/rapports-${slug}${qs}`;

      const res = await fetch(url, {
        headers: {
          Accept: "application/ld+json",
          ...(tokenSuivi ? { Authorization: `Bearer ${tokenSuivi}` } : {}),
        },
      });

      if (!res.ok) {
        console.error("Erreur API", res.status);
        setRapports([]);
        setPagination(buildPagination(pageOverride || page, perPage, 0));
      } else {
        const raw = await res.json();
        let list;
        
        if (Array.isArray(raw)) {
          list = raw;
        } else if (raw && Array.isArray(raw["member"])) {
          list = raw["member"];
        } else {
          list = [];
        }

        const adapted = list.map(adaptRapport);

        // totalItems si fourni par API Platform
        const totalItems = Number(raw?.["hydra:totalItems"] ?? raw?.totalItems ?? adapted.length) || 0;

        setRapports(adapted);
        setPagination(buildPagination(pageOverride || page, perPage, totalItems));
      }
    } catch (e) {
      console.error(e);
      setRapports([]);
      setPagination(buildPagination(pageOverride || page, perPage, 0));
    } finally {
      setLoading(false);
    }
  }, [buildQueryString, page, perPage]);

  /** Premier chargement */
  useEffect(() => {
    fetchRapports(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRapports]);

  /** Refetch quand perPage change (reset page=1) */
  useEffect(() => {
    setPage(1);
    fetchRapports(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  /** Refetch quand tri change (conserve page courante) */
  useEffect(() => {
    fetchRapports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderMission, orderCreatedAt]);

  /**
   * Refetch dès qu’un filtre change (UX dynamique)
   * Reset à page=1 pour éviter des pages vides
   */
  useEffect(() => {
    setPage(1);
    fetchRapports(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, institution, typeAudit, structure, audit, typeRevu, inspecteur, localite, date]);

  /** Handlers pagination : pas de <a href="/"> => pas de reload ni perte de focus */
  const goFirst = () => { if (pagination.firstPage !== pagination.currentPage) { setPage(pagination.firstPage); fetchRapports(pagination.firstPage); } };
  const goPrev  = () => { if (pagination.prevPage)  { setPage(pagination.prevPage);  fetchRapports(pagination.prevPage);  } };
  const goNext  = () => { if (pagination.nextPage)  { console.log(pagination.nextPage,'pagination.nextPage'); setPage(pagination.nextPage);  fetchRapports(pagination.nextPage);  } };
  const goLast  = () => { if (pagination.lastPage !== pagination.currentPage) { setPage(pagination.lastPage); fetchRapports(pagination.lastPage); } };

  /** Reset filtres */
  const resetFilters = () => {
    setSearch("");
    setInstitution(null);
    setTypeAudit(null);
    setStructure(null);
    setAudit(null);
    setTypeRevu(null);
    setInspecteur(null);
    setLocalite(null);
    setDate("");
    setOrderMission(null);
    setOrderCreatedAt('desc');
    setPage(1);
    fetchRapports(1);
  };

  /** open PDF in modal */
  const readDoc = (url) => {
    const abs = toAbsoluteUrl(url);
    if (!abs) return;
    console.log("Opening viewer for", abs);
    setViewerSource(abs);
    setShowViewer(true);
  };

  async function secureDownload(url) {
    try {
      const { blob } = await fetchBlob(url);
      const name = url.split('/').pop() || 'document';
      downloadBlob(blob, name);
    } catch (e) {
      notify('Erreur', "Téléchargement impossible.", 'error');
    }
  }

  // options transformées en {value,label}
  const instOpts = (institutionsOptions || []).map(p => asOption(iri.structure(p.id), p.title + (p.acronym ? ` (${p.acronym})` : '')));
  const typeAuditOpts = (typesAuditOptions || []).map(p => asOption(iri.typeAudit(p.id), p.title));
  const auditsOpts = (auditsOptions || []).map(p => asOption(iri.audit(p.id), p.title + (p.acronym ? ` (${p.acronym})` : '')));
  const typeRevueOpts = (typesRevuOptions || []).map(p => asOption(iri.typeRevue(p.id), p.title));
  const inspecteursOpts = (inspecteursOptions || []).map(p => asOption(iri.user(p.id), p.name));
  const localitesOpts = (localitesOptions || []).map(p => asOption(iri.localite(p.id), p.title));

  /** ===================== Chargement des options ===================== */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Type audits
        const r1 = await fetch(`${apiBase}/type_audits`, { headers: authHeaders() });
        setTypesAuditOptions(r1.ok ? await r1.json() : []);

        // Audits
        const r2 = await fetch(`${apiBase}/audits`, { headers: authHeaders() });
        setAuditsOptions(r2.ok ? await r2.json() : []);

        // Type revues
        const r3 = await fetch(`${apiBase}/type_revues`, { headers: authHeaders() });
        setTypesRevuOptions(r3.ok ? await r3.json() : []);

        // Utilisateurs
        const r4 = await fetch(`${apiBase}/users`, { headers: authHeaders() });
        setInspecteursOptions(r4.ok ? await r4.json() : []);

        // Structures
        const r5 = await fetch(`${apiBase}/structures`, { headers: authHeaders() });
        const st = r5.ok ? await r5.json() : [];
        setInstitutionsOptions(Array.isArray(st) ? st : []);

        // Localités
        const rD = await fetch(`${apiBase}/localites`, { headers: authHeaders() });
        setLocalitesOptions(rD.ok ? await rD.json() : []);

      } catch (e) {
        // notify("Erreur", "Impossible de charger les listes", "danger");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /**
   * corps principal
   */
    return (
      <Layout>

          <div>
            <div className="content-body">
              <div className="d-flex justify-content-between align-items-center my-3">
                <div>
                  <h4 style={{ textTransform: "uppercase" }}>Liste des rapports</h4>
                </div>

                <div>
                  <Link to="/suivi-recommandations/stats">
                    <button type="button" className="btn btn-sm btn-secondary" style={{ border: "none", textTransform: "uppercase", }}>
                      Voir les statistiques
                    </button>
                  </Link>
                  <Link to="/suivi-recommandations/add">
                    <button className="page-title" style={{ border: "none" }}>
                      soumettre un rapport
                    </button>
                  </Link>
                </div>
              </div>

              {/* Barre de filtres */}
              <form
                className="card p-3 mb-3"
                style={{ backgroundColor: "#fff", border: "1px solid #ddd",marginRight: "10px",marginLeft: "5px" }}
                onSubmit={(e)=>e.preventDefault()} // pas de submit => pas de reload
              >
                <div className="mb-2" style={{ fontSize: "0.9rem", color: "#6c757d" }}>
                  Utiliser le filtre afin de vite retrouver un rapport.
                </div>

                <div className="row g-2 align-items-end">
                  {/* Recherche par mission */}
                  <div className="col-md-4">
                    <label style={{ fontSize: "0.8rem" }} className="text-muted">
                      Rechercher par mission...
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fa fa-search" />
                      </span>
                      <input
                        name="mission"
                        className="form-control"
                        placeholder="Rechercher par mission..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Date (facultatif selon API) */}
                  <div className="col-md-3 col-lg-2">
                    <label style={{ fontSize: "0.8rem" }} className="text-muted">
                      Date
                    </label>
                    <DatePicker
                      name="date"
                      className="form-control"
                      onChange={(e) => setDate(e)}
                      value={date}
                      format="dd/MM/y"
                    />
                  </div>

                  {/* Institution */}
                  <div className="col-md-3 col-lg-2">
                    <label style={{ fontSize: "0.8rem" }} className="text-muted">
                      Institution / Structure / Entité
                    </label>
                    <Select
                      name="institution"
                      placeholder="Choisir…"
                      noOptionsMessage={() => "Aucune donnée disponible"}
                      options={instOpts}
                      onChange={(opt) => setInstitution(opt)}
                      value={institution}
                      isSearchable
                    />
                  </div>

                  {/* Type d'audit (mappé vers programme[] dans l'exemple fourni) */}
                  {/* <div className="col-md-3 col-lg-2">
                    <label style={{ fontSize: "0.8rem" }} className="text-muted">
                      Type d'audit
                    </label>
                    <Select
                      name="typeAudit"
                      placeholder="Choisir…"
                      noOptionsMessage={() => "Aucune donnée disponible"}
                      options={typeAuditOpts}
                      onChange={(opt) => setTypeAudit(opt)}
                      value={typeAudit}
                      isSearchable
                    />
                  </div> */}

                  {/* Audit */}
                  {/* <div className="col-md-3 col-lg-2">
                    <label style={{ fontSize: "0.8rem" }} className="text-muted">
                      Audit
                    </label>
                    <Select
                      name="audit"
                      placeholder="Choisir…"
                      noOptionsMessage={() => "Aucune donnée disponible"}
                      options={auditsOpts}
                      onChange={(opt) => setAudit(opt)}
                      value={audit}
                      isSearchable
                    />
                  </div> */}

                  {/* Type revu */}
                  {/* <div className="col-md-3 col-lg-2">
                    <label style={{ fontSize: "0.8rem" }} className="text-muted">
                      Type revu
                    </label>
                    <Select
                      name="typeRevue"
                      placeholder="Choisir…"
                      noOptionsMessage={() => "Aucune donnée disponible"}
                      options={typeRevueOpts}
                      onChange={(opt) => setTypeRevu(opt)}
                      value={typeRevu}
                      isSearchable
                    />
                  </div> */}

                  {/* Inspecteur */}
                  <div className="col-md-3 col-lg-2">
                    <label style={{ fontSize: "0.8rem" }} className="text-muted">
                      Inspecteur
                    </label>
                    <Select
                      name="inspecteur"
                      placeholder="Choisir…"
                      noOptionsMessage={() => "Aucune donnée disponible"}
                      options={inspecteursOpts}
                      onChange={(opt) => setInspecteur(opt)}
                      value={inspecteur}
                      isSearchable
                    />
                  </div>

                  {/* Localisation */}
                  {/* <div className="col-md-3 col-lg-2">
                    <label style={{ fontSize: "0.8rem" }} className="text-muted">
                      Localisation
                    </label>
                    <Select
                      name="localite"
                      placeholder="Choisir…"
                      noOptionsMessage={() => "Aucune donnée disponible"}
                      options={localitesOpts}
                      onChange={(opt) => setLocalite(opt)}
                      value={localite}
                      isSearchable
                    />
                  </div> */}

                  {/* Tri simple */}
                  {/* <div className="col-md-3 col-lg-2">
                    <label style={{ fontSize: "0.8rem" }} className="text-muted">Tri mission</label>
                    <select className="form-control" value={orderMission || ""} onChange={(e)=>setOrderMission(e.target.value || null)}>
                      <option value="">—</option>
                      <option value="asc">Mission ↑</option>
                      <option value="desc">Mission ↓</option>
                    </select>
                  </div>
                  <div className="col-md-3 col-lg-2">
                    <label style={{ fontSize: "0.8rem" }} className="text-muted">Tri date</label>
                    <select className="form-control" value={orderCreatedAt || ""} onChange={(e)=>setOrderCreatedAt(e.target.value || null)}>
                      <option value="">—</option>
                      <option value="asc">Création ↑</option>
                      <option value="desc">Création ↓</option>
                    </select>
                  </div> */}

                  {/* Bouton Réinitialiser */}
                  <div className="col-md-3 col-lg-2 d-flex gap-2 actions-bar">
                    <button
                      type="button"
                      className="btn btn-secondary w-100"
                      style={{ whiteSpace: "nowrap", float: "right" }}
                      onClick={resetFilters}
                      disabled={loading}
                    >
                      Réinitialiser
                    </button>
                  </div>
                </div>
              </form>

              <div className="container-fluid mt-3 mb-3">
                <div className="row mt-3">
                  <div className="col-sm-12 col-md-12 p-2">
                    <table className="table table-bordered table-striped">
                      <thead
                        style={{ textTransform: "uppercase", color: "#fff" }}
                        className="bckColorDark"
                      >
                        <tr>
                          <th>N°</th>
                          <th style={{cursor: 'pointer'}} 
                              onMouseLeave={() => {
                                setMouseOver(null);
                              }} 
                              onMouseOver={() => {
                                setMouseOver('createdAt');
                              }}
                              onClick={() => {
                                // cycle asc -> desc -> null
                                if (orderCreatedAt === 'asc') {
                                  setOrderCreatedAt('desc');
                                // } else if (orderCreatedAt === 'desc') {
                                //   setOrderCreatedAt(null);
                                } else {
                                  setOrderCreatedAt('asc');
                                } 
                                // reset l'autre ordre
                                setOrderMission(null);
                              }}
                            > 
                              Date 
                              <span style={{float: 'right'}}> ↓↑
                                {/* {(mouseOver !== 'createdAt' && orderCreatedAt === 'asc') ? '↑' 
                                : (mouseOver !== 'createdAt' && orderCreatedAt === 'desc') ? '↓' 
                                : (mouseOver === 'createdAt' && orderCreatedAt === 'asc') ? '↓' 
                                : (mouseOver === 'createdAt' && orderCreatedAt === 'desc') ? '↑'
                                : '↓↑'}  */}
                              </span>
                          </th>
                          <th style={{cursor: 'pointer'}} 
                              onMouseLeave={() => {
                                setMouseOver(null);
                              }} 
                              onMouseOver={() => {
                                setMouseOver('mission');
                              }}
                              onClick={() => {
                                // cycle asc -> desc -> null
                                if (orderMission === 'asc') {
                                  setOrderMission('desc');
                                // } else if (orderMission === 'desc') {
                                //   setOrderMission(null);
                                } else {
                                  setOrderMission('asc');
                                } 
                                // reset l'autre ordre
                                setOrderCreatedAt(null);
                              }}>
                              Mission 
                              <span style={{float: 'right'}}> ↓↑
                                {/* {(mouseOver !== 'mission' && orderCreatedAt === 'asc') ? '↑' 
                                : (mouseOver !== 'mission' && orderCreatedAt === 'desc') ? '↓' 
                                : (mouseOver === 'mission' && orderCreatedAt === 'asc') ? '↓' 
                                : (mouseOver === 'mission' && orderCreatedAt === 'desc') ? '↑'
                                : '↓↑'}   */}
                              </span>
                          </th>
                          <th>Inspecteur</th>
                          <th>Structure</th>
                          <th><span style={{float: "right"}}>Actions</span></th>
                        </tr>
                      </thead>

                      <tbody>
                        {rapports && rapports.map((rapport, idx) => (
                          <tr key={rapport.id ?? idx}>
                            <td>
                              {pagination.from ? pagination.from + idx : idx + 1}
                            </td>

                            <td>
                              {rapport.createdAt &&
                                dateFormat(
                                  rapport.createdAt,
                                  "DD/MM/YYYY à HH:mm"
                                )}
                            </td>

                            <td>
                              <strong>
                                <Link to={`/suivi-recommandations/read/${rapport.id}`}>
                                  {rapport.libelle}
                                </Link>
                              </strong>
                            </td>

                            <td>
                              {`${(rapport.senderAgent?.prenom || "").toUpperCase()} ${(rapport.senderAgent?.nom || "").toUpperCase()}`.trim()}
                            </td>

                            <td>{rapport.senderStructure?.intitule || "—"}</td>

                            <td>
                              <RowActions
                                rapport={rapport}
                                onRead={(r) => readDoc(r)}
                                openCommentModal={openCommentModal}
                                secureDownload={secureDownload}
                              />
                            </td>
                          </tr>
                        ))}

                        {!rapports?.length && (
                          <tr>
                            <td colSpan={6} className="text-center">
                              Aucun résultat
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Pagination SPA : boutons => pas de reload ni perte de focus */}
                    <div>
                      <nav aria-label="" className=" mt-3">
                        <ul className="pagination w-25 mx-auto">
                          <li className={pagination.hasPrev ? 'page-item' : 'page-item disabled'}>
                            <button type="button" className="page-link" onClick={goFirst} aria-disabled={!pagination.hasPrev}>
                              <i className="fas fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li className={pagination.hasPrev ? 'page-item' : 'page-item disabled'}>
                            <button type="button" className="page-link" onClick={goPrev}>Précédent</button>
                          </li>
                          <li className={pagination.hasNext ? 'page-item' : 'page-item disabled'}>
                            <button type="button" className="page-link" onClick={goNext}>Suivant</button>
                          </li>
                          <li className={pagination.hasNext ? 'page-item' : 'page-item disabled'}>
                            <button type="button" className="page-link" onClick={goLast}>
                              <i className="fas fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>

                    <div className="d-flex justify-content-end mt-2">
                      <select
                        className="form-control form-control-sm"
                        style={{ width: 120 }}
                        value={perPage}
                        onChange={(e) => setPerPage(Number(e.target.value) || 10)}
                      >
                        {[5, 10, 20, 50].map((n) => (
                          <option key={n} value={n}>
                            {n}/page
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal commentaire */}
              <Modal
                show={commentOpen}
                large={false}
                size="modal-md"
                title="Ajouter un commentaire"
                body={<>
                    <span>
                      <div className="form-group my-3">
                        <UploadPdfDropzone
                          label="Ajouter un fichier"
                          hint="Formats acceptés PDF (max. 200MB)"
                          maxMB={200}
                          multiple={false}
                          value={commentDraft?.file ? { id: commentDraft.fileId, name: commentDraft.fileName } : null}
                          apiBase={apiBase}
                          authHeaders={authHeaders}
                          onUploaded={(fileOrNull) => {
                            if (!fileOrNull) {
                              setCommentDraft((p) => ({ ...p, file: "", fileId: "", fileName: "" }));
                              return;
                            }
                            setCommentDraft((p) => ({
                              ...p,
                              file: `/api/media/${fileOrNull.id}`.replace("//api", "/api"),
                              fileId: fileOrNull.id,
                              fileName: fileOrNull.name || "document.pdf",
                            }));
                          }}
                        />
                      </div>

                      <div className="form-group mt-3" id="comment-modal-body">
                        <label className="mb-2 label">Commentaire *</label>
                        <Quill
                          theme="snow"
                          bounds="#comment-modal-body"
                          scrollingContainer="#comment-modal-body"
                          modules={quillModules}
                          value={commentDraft?.content || ""}
                          onChange={(val) => {setCommentDraft((p) => ({ ...p, content: val }))}}
                        />
                      </div>
                      <div className="modal-footer text-center">
                        <button type="button" className="btn btn-secondary" onClick={closeCommentModal}>
                          Annuler
                        </button>
                        <button
                          type="button"
                          className="btn btn-success"
                          disabled={!canSaveComment || commentSaving}
                          onClick={() => saveComment?.(commentDraft)}
                        >
                          {commentSaving ? "Envoi..." : "Enregistrer"}
                        </button>
                      </div>
                    </span>
                  </>}
                onClose={closeCommentModal}
              />

              <Modal
                large
                size="modal-lg"
                show={showViewer && !!viewerSource}
                title="Lecture du Document"
                body={<SecureFileViewer source={viewerSource} height={700} />}
                onClose={() => { setShowViewer(false); setViewerSource(null); }}
              />
            </div>
          </div>
      </Layout>
    );

}
